import React from "react";
import { statusPillClasses, priorityClasses, formatDuration, formatDate, statusDotClasses } from "@/lib/format";
import { applyFilters } from "@/components/FilterBar";
import { AvatarGroup } from "@/components/inline/AssigneeInline";
import { Calendar, Flag, Hourglass } from "lucide-react";

export default function KanbanView({ list, tasks, users, filters, canEdit, onOpenTask, onUpdateTask }) {
  const statuses = (list.statuses || []).slice().sort((a, b) => a.order - b.order);
  const filtered = applyFilters(tasks, filters);

  const grouped = statuses.map((s) => ({
    status: s,
    tasks: filtered.filter((t) => t.status_id === s.id),
  }));

  const onDragStart = (e, taskId) => {
    e.dataTransfer.setData("text/plain", taskId);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDragOver = (e) => { if (canEdit) e.preventDefault(); };

  const onDrop = async (e, statusId) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("text/plain");
    if (!taskId) return;
    const task = tasks.find((t) => t.id === taskId);
    if (!task || task.status_id === statusId) return;
    try {
      await onUpdateTask(taskId, { status_id: statusId });
    } catch { /* error toasted */ }
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 items-start" data-testid="kanban-view">
      {grouped.map(({ status, tasks: cards }) => (
        <div
          key={status.id}
          className="kanban-col w-72 shrink-0 bg-neutral-100/70 border border-neutral-200 rounded-md flex flex-col"
          onDragOver={onDragOver}
          onDrop={(e) => onDrop(e, status.id)}
          data-testid={`kanban-column-${status.id}`}
        >
          <div className="px-3 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${statusPillClasses(status.color)}`}>
                {status.name}
              </span>
              <span className="font-mono-data text-[11px] text-neutral-500 font-semibold">{cards.length}</span>
            </div>
          </div>
          <div className="px-2 pb-2 space-y-2 flex-1">
            {cards.length === 0 && (
              <div className="text-[11px] text-neutral-400 text-center py-6 border-2 border-dashed border-neutral-200 rounded-md">
                Drop tasks here
              </div>
            )}
            {cards.map((t) => {
              const pri = t.priority;
              return (
                <div
                  key={t.id}
                  draggable={canEdit}
                  onDragStart={(e) => onDragStart(e, t.id)}
                  onClick={() => onOpenTask(t.id)}
                  className="bg-white border border-neutral-200 rounded-md p-3 hover:shadow-sm hover:border-neutral-300 cursor-pointer transition-all"
                  data-testid={`kanban-card-${t.id}`}
                >
                  <div className="flex items-start gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${statusDotClasses(status.color)}`} />
                    <div className="text-sm font-medium leading-snug">{t.title}</div>
                  </div>
                  <div className="flex items-center justify-between text-[11px] gap-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border font-semibold uppercase tracking-wider ${priorityClasses(pri)}`}>
                      <Flag className="w-2.5 h-2.5" fill={pri !== "low" ? "currentColor" : "none"} />
                      {pri}
                    </span>
                    <div className="flex items-center gap-2 text-neutral-500">
                      {t.due_date && (
                        <span className="flex items-center gap-1 font-mono-data">
                          <Calendar className="w-3 h-3" />{formatDate(t.due_date)}
                        </span>
                      )}
                      {t.total_time_seconds > 0 && (
                        <span className="flex items-center gap-1 font-mono-data">
                          <Hourglass className="w-3 h-3" />{formatDuration(t.total_time_seconds)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end mt-2">
                    <AvatarGroup task={t} users={users} max={3} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
